(() => {
  const isLocalRevealPreview = ['127.0.0.1', 'localhost', '::1'].includes(window.location.hostname);

  const resetMobileViewport = () => {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    document.getElementById('MainContent')?.scrollTo(0, 0);
    document.querySelectorAll('[data-ramoking-access-gate]').forEach((gate) => gate.scrollTo(0, 0));
    window.scrollTo(0, 0);
  };

  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  window.addEventListener('pageshow', resetMobileViewport);
  window.addEventListener('load', resetMobileViewport);

  const initializeAccessGate = (root) => {
    if (!root || root.dataset.ramokingAccessGateReady === 'true') return;

    root.dataset.ramokingAccessGateReady = 'true';

    const form = root.querySelector('.ramoking-gate__form');
    if (!form) return;

    const interests = Array.from(form.querySelectorAll('[data-ramoking-interest]'));
    const tagsInput = form.querySelector('[data-ramoking-tags]');
    const error = form.querySelector('[data-ramoking-choice-error]');
    const choiceGroup = form.querySelector('[data-ramoking-choice-group]');
    const selectionPrompt = form.querySelector('[data-ramoking-selection-prompt]');
    const terminalInput = form.querySelector('[data-ramoking-terminal-input]');
    const terminalMirror = form.querySelector('[data-ramoking-terminal-mirror]');
    const terminal = terminalInput?.closest('.ramoking-gate__terminal');
    const submitButton = form.querySelector('.ramoking-gate__submit');
    const reveal = root.querySelector('[data-ramoking-reveal]');
    const revealClock = reveal?.querySelector('[data-ramoking-reveal-clock]');
    const revealLeft = reveal?.querySelector('[data-ramoking-reveal-left]');
    const revealRight = reveal?.querySelector('[data-ramoking-reveal-right]');
    const flashLayer = root.querySelector('[data-ramoking-flash]');
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    let revealClockTimer;
    let revealAudioContext;
    let revealFlashTimers = [];
    let terminalCursorFrame;
    let terminalCursorSignature = '';
    const defaultSelectionPrompt = selectionPrompt?.textContent.trim() || '';
    const requiredSelectionPrompt = error?.textContent.trim() || 'CHOOSE ONE.';

    const revealFlashPattern = [120, 215, 310].map((at) => ({
      at,
      duration: 24,
    }));

    const syncTerminalCursor = () => {
      if (!terminalInput || !terminalMirror) return;

      const selectionStart = terminalInput.selectionStart ?? terminalInput.value.length;
      const selectionEnd = terminalInput.selectionEnd ?? selectionStart;
      const cursorPosition = terminalInput.selectionDirection === 'backward' ? selectionStart : selectionEnd;
      const signature = `${terminalInput.value}\u0000${cursorPosition}\u0000${terminalInput.scrollLeft}`;

      if (signature === terminalCursorSignature) return;
      terminalCursorSignature = signature;

      terminalMirror.textContent = terminalInput.value.slice(0, cursorPosition);
      terminalMirror.parentElement.style.setProperty('--terminal-scroll', `${-terminalInput.scrollLeft}px`);
      terminal?.classList.toggle('ramoking-gate__terminal--has-value', terminalInput.value.length > 0);
    };

    const trackTerminalCursor = () => {
      if (!terminalInput || !terminalMirror) return;

      syncTerminalCursor();
      if (document.activeElement === terminalInput) {
        terminalCursorFrame = window.requestAnimationFrame(trackTerminalCursor);
      }
    };

    const scheduleTerminalCursorSync = () => {
      window.cancelAnimationFrame(terminalCursorFrame);
      terminalCursorFrame = window.requestAnimationFrame(trackTerminalCursor);
    };

    const clearChoiceRequired = () => {
      if (error) error.hidden = true;

      choiceGroup?.classList.remove('is-choice-required');
      choiceGroup?.removeAttribute('aria-invalid');

      if (selectionPrompt) {
        selectionPrompt.classList.remove('is-choice-required');
        selectionPrompt.textContent = defaultSelectionPrompt;
      }
    };

    const showChoiceRequired = () => {
      if (error) error.hidden = false;

      choiceGroup?.setAttribute('aria-invalid', 'true');
      choiceGroup?.classList.remove('is-choice-required');

      if (selectionPrompt) {
        selectionPrompt.textContent = requiredSelectionPrompt;
        selectionPrompt.classList.remove('is-choice-required');
      }

      // Restart the visual cue when ENTER is pressed again without a selection.
      void choiceGroup?.offsetWidth;
      choiceGroup?.classList.add('is-choice-required');
      selectionPrompt?.classList.add('is-choice-required');
      choiceGroup?.focus({ preventScroll: true });
      choiceGroup?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const syncTags = () => {
      if (!tagsInput) return;

      const tags = ['newsletter', 'ramoking_waitlist'];
      interests.forEach((interest) => {
        if (interest.checked) tags.push(interest.value);
      });
      tagsInput.value = tags.join(',');

      if (interests.some((interest) => interest.checked)) clearChoiceRequired();
    };

    const buildRevealSnapshot = () => {
      const snapshotPage = document.createElement('div');
      snapshotPage.className = 'ramoking-gate ramoking-reveal__snapshot-page';
      snapshotPage.setAttribute('aria-hidden', 'true');

      const colorScheme = root.getAttribute('data-color-scheme');
      if (colorScheme) snapshotPage.setAttribute('data-color-scheme', colorScheme);

      ['.ramoking-gate__backdrop', '.ramoking-gate__header', '.ramoking-gate__stage'].forEach(
        (selector) => {
          const element = root.querySelector(selector);
          if (element) snapshotPage.append(element.cloneNode(true));
        }
      );

      const sourceControls = Array.from(root.querySelectorAll('.ramoking-gate__stage input'));
      const snapshotControls = Array.from(snapshotPage.querySelectorAll('input'));
      sourceControls.forEach((source, index) => {
        const copy = snapshotControls[index];
        if (!copy) return;
        copy.value = source.value;
        copy.checked = source.checked;
      });

      snapshotPage.querySelectorAll('*').forEach((element) => {
        element.removeAttribute('id');
        element.removeAttribute('for');
        element.removeAttribute('name');
        element.removeAttribute('tabindex');

        Array.from(element.attributes).forEach((attribute) => {
          if (
            attribute.name.startsWith('x-') ||
            attribute.name.startsWith('@') ||
            attribute.name.startsWith(':')
          ) {
            element.removeAttribute(attribute.name);
          }
        });
      });

      return snapshotPage;
    };

    const updateRevealClock = () => {
      if (!revealClock) return;

      const now = new Date();
      const time = [now.getHours(), now.getMinutes(), now.getSeconds()]
        .map((part) => String(part).padStart(2, '0'))
        .join(':');

      revealClock.textContent = time;
      revealClock.dataset.time = time;
      revealClock.dateTime = now.toISOString();
    };

    const prepareRevealAudio = () => {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;

      revealAudioContext ||= new AudioContextClass();
      if (revealAudioContext.state === 'suspended') {
        revealAudioContext.resume().catch(() => {});
      }
    };

    const playElectricTick = (index) => {
      const context = revealAudioContext;
      if (!context || context.state !== 'running') return;

      const now = context.currentTime;
      const master = context.createGain();
      const spark = context.createOscillator();
      const sparkFilter = context.createBiquadFilter();
      const noise = context.createBufferSource();
      const noiseFilter = context.createBiquadFilter();
      const noiseGain = context.createGain();
      const noiseLength = Math.ceil(context.sampleRate * 0.026);
      const noiseBuffer = context.createBuffer(1, noiseLength, context.sampleRate);
      const noiseData = noiseBuffer.getChannelData(0);

      for (let sample = 0; sample < noiseLength; sample += 1) {
        const decay = 1 - sample / noiseLength;
        noiseData[sample] = (Math.random() * 2 - 1) * decay * decay;
      }

      master.gain.setValueAtTime(0.0001, now);
      master.gain.exponentialRampToValueAtTime(0.055 + (index % 3) * 0.006, now + 0.0015);
      master.gain.exponentialRampToValueAtTime(0.0001, now + 0.043);

      spark.type = 'square';
      spark.frequency.setValueAtTime(3900 + (index % 4) * 410, now);
      spark.frequency.exponentialRampToValueAtTime(760 + (index % 2) * 120, now + 0.032);

      sparkFilter.type = 'bandpass';
      sparkFilter.frequency.setValueAtTime(3100 + (index % 3) * 320, now);
      sparkFilter.Q.setValueAtTime(3.8, now);

      noise.buffer = noiseBuffer;
      noiseFilter.type = 'highpass';
      noiseFilter.frequency.setValueAtTime(2300 + (index % 3) * 280, now);
      noiseGain.gain.setValueAtTime(0.26, now);
      noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.027);

      spark.connect(sparkFilter).connect(master);
      noise.connect(noiseFilter).connect(noiseGain).connect(master);
      master.connect(context.destination);

      spark.start(now);
      spark.stop(now + 0.045);
      noise.start(now);
      noise.stop(now + 0.028);
    };

    const setFlashActive = (active) => {
      if (!flashLayer) return;
      flashLayer.hidden = !active;
    };

    const beginRevealOpening = () => {
      if (!reveal) return;

      terminalInput?.blur();
      themeColorMeta?.setAttribute('content', '#70000f');
      document.documentElement.classList.add('ramoking-reveal-lock');
      reveal.hidden = false;
      reveal.setAttribute('aria-hidden', 'false');
      void reveal.offsetWidth;
      reveal.classList.add('is-active', 'is-opening');
    };

    const startRevealFlashBurst = () => {
      if (!reveal) return;

      revealFlashTimers.forEach((timer) => window.clearTimeout(timer));
      revealFlashTimers = [];
      reveal.classList.remove('is-opening');
      setFlashActive(false);

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        beginRevealOpening();
        return;
      }

      revealFlashPattern.forEach(({ at, duration }, index) => {
        const isFinalFlash = index === revealFlashPattern.length - 1;

        revealFlashTimers.push(
          window.setTimeout(() => {
            setFlashActive(true);
            if (isFinalFlash) {
              beginRevealOpening();
            }
            playElectricTick(index);
          }, at),
          window.setTimeout(() => {
            setFlashActive(false);
          }, at + duration)
        );
      });
    };

    const openRevealPreview = () => {
      if (!reveal || !revealLeft || !revealRight) return;

      prepareRevealAudio();
      window.clearInterval(revealClockTimer);
      reveal.classList.remove('is-active', 'is-opening');
      document.documentElement.classList.remove('ramoking-reveal-lock');
      setFlashActive(false);
      revealLeft.replaceChildren(buildRevealSnapshot());
      revealRight.replaceChildren(buildRevealSnapshot());
      updateRevealClock();
      revealClockTimer = window.setInterval(updateRevealClock, 1000);

      reveal.hidden = true;
      reveal.setAttribute('aria-hidden', 'true');
      startRevealFlashBurst();
    };

    const submitSignupInBackground = () => {
      if (isLocalRevealPreview) return;

      const frame = document.createElement('iframe');
      const targetName = `RamokingSignupResult-${Date.now()}`;
      const originalTarget = form.getAttribute('target');

      frame.name = targetName;
      frame.hidden = true;
      frame.setAttribute('aria-hidden', 'true');
      frame.setAttribute('tabindex', '-1');
      root.append(frame);
      form.setAttribute('target', targetName);

      try {
        HTMLFormElement.prototype.submit.call(form);
      } catch (_error) {
        // Preserve signup reliability if a browser refuses the background target.
        if (originalTarget === null) {
          form.removeAttribute('target');
        } else {
          form.setAttribute('target', originalTarget);
        }
        HTMLFormElement.prototype.submit.call(form);
        return;
      }

      if (originalTarget === null) {
        form.removeAttribute('target');
      } else {
        form.setAttribute('target', originalTarget);
      }
    };

    if (reveal) {
      const runReveal = (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();

        if (!form.checkValidity()) {
          form.reportValidity();
          return;
        }

        if (interests.length > 0 && !interests.some((interest) => interest.checked)) {
          showChoiceRequired();
          return;
        }

        syncTags();
        openRevealPreview();
        submitSignupInBackground();
      };

      form.dataset.ramokingRevealReady = 'true';
      form.addEventListener('submit', runReveal, true);
      submitButton?.addEventListener('click', runReveal, true);
    }

    interests.forEach((interest) => interest.addEventListener('change', syncTags));

    if (terminalInput) {
      ['input', 'keyup', 'click', 'pointerup', 'touchend', 'select', 'scroll'].forEach((eventName) => {
        terminalInput.addEventListener(eventName, scheduleTerminalCursorSync, { passive: true });
      });

      terminalInput.addEventListener('focus', () => {
        scheduleTerminalCursorSync();
        requestAnimationFrame(() => {
          resetMobileViewport();
        });
      });

      document.addEventListener('selectionchange', () => {
        if (document.activeElement === terminalInput) scheduleTerminalCursorSync();
      });

      terminalInput.addEventListener('blur', () => {
        window.cancelAnimationFrame(terminalCursorFrame);
        syncTerminalCursor();
        requestAnimationFrame(resetMobileViewport);
        window.setTimeout(resetMobileViewport, 250);
      });
    }

    form.addEventListener('submit', (event) => {
      if (interests.length > 0 && !interests.some((interest) => interest.checked)) {
        event.preventDefault();
        showChoiceRequired();
        return;
      }

      syncTags();

    });

    syncTags();
    syncTerminalCursor();
    document.fonts?.ready.then(scheduleTerminalCursorSync).catch(() => {});
    resetMobileViewport();
  };

  const initializeAllAccessGates = (scope = document) => {
    scope.querySelectorAll('[data-ramoking-access-gate]').forEach(initializeAccessGate);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAllAccessGates());
  } else {
    initializeAllAccessGates();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAllAccessGates(event.target));
})();
